import { useEffect, useState } from 'react';
import { useAuth } from '../../hooks/useAuth';
import { supabase } from '../../lib/supabase';
import { Spinner } from '../../components/design-system';
import { staysHitByBlock, type BookedStay } from '../../lib/hostBlocks';
import type { HostUnavailability } from '../../types';

const WEEKDAYS = ['S', 'M', 'T', 'W', 'T', 'F', 'S'];
const MONTHS = ['January', 'February', 'March', 'April', 'May', 'June', 'July', 'August', 'September', 'October', 'November', 'December'];

function ymd(d: Date): string {
  return `${d.getFullYear()}-${String(d.getMonth() + 1).padStart(2, '0')}-${String(d.getDate()).padStart(2, '0')}`;
}

function todayYmd(): string {
  const d = new Date();
  d.setHours(0, 0, 0, 0);
  return ymd(d);
}

/** Build a 6-row grid of dates for the month containing `cursor` (Sun-start). */
function monthGrid(cursor: Date): (Date | null)[] {
  const year = cursor.getFullYear();
  const month = cursor.getMonth();
  const first = new Date(year, month, 1);
  const startPad = first.getDay();
  const daysInMonth = new Date(year, month + 1, 0).getDate();
  const cells: (Date | null)[] = [];
  for (let i = 0; i < startPad; i++) cells.push(null);
  for (let day = 1; day <= daysInMonth; day++) cells.push(new Date(year, month, day));
  while (cells.length % 7 !== 0) cells.push(null);
  return cells;
}

function formatRange(start: string, end: string): string {
  const s = new Date(start + 'T12:00:00');
  const e = new Date(end + 'T12:00:00');
  const opts: Intl.DateTimeFormatOptions = { month: 'short', day: 'numeric' };
  if (start === end) return s.toLocaleDateString('en', { month: 'short', day: 'numeric', year: 'numeric' });
  const sameYear = s.getFullYear() === e.getFullYear();
  return `${s.toLocaleDateString('en', opts)} – ${e.toLocaleDateString('en', { ...opts, year: 'numeric' })}${sameYear ? '' : ` ${s.getFullYear()}`}`;
}

// Blocking dates never cancels a trip a host already has; this finds the ones the
// new block lands on so the host can be told. Best effort: no warning if it fails.
async function bookedStaysHit(hostId: string, start: string, end: string): Promise<BookedStay[]> {
  try {
    const { data } = await supabase
      .from('matches')
      .select('trip_legs(city, start_date, days), taste_portraits(destinations, start_date, trip_days)')
      .eq('host_id', hostId)
      .in('status', ['pending', 'accepted']);
    const one = <T,>(v: T | T[] | null | undefined): T | null => (Array.isArray(v) ? v[0] ?? null : v ?? null);
    const stays: BookedStay[] = [];
    for (const row of (data ?? []) as unknown as Record<string, unknown>[]) {
      const leg = one(row.trip_legs as { city: string; start_date: string; days: number } | null);
      const portrait = one(row.taste_portraits as { destinations: string[] | null; start_date: string | null; trip_days: number | null } | null);
      if (leg) stays.push({ city: leg.city, start: leg.start_date, days: leg.days });
      else if (portrait?.start_date) stays.push({ city: portrait.destinations?.[0] ?? null, start: portrait.start_date, days: portrait.trip_days ?? 1 });
    }
    return staysHitByBlock(start, end, stays);
  } catch {
    return [];
  }
}

export function Availability() {
  const { user } = useAuth();
  const [blocks, setBlocks] = useState<HostUnavailability[]>([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [cursor, setCursor] = useState(() => { const d = new Date(); return new Date(d.getFullYear(), d.getMonth(), 1); });
  const [pendingStart, setPendingStart] = useState<string | null>(null);
  const [clash, setClash] = useState<BookedStay[]>([]);

  async function load() {
    if (!user) return;
    const { data } = await supabase
      .from('host_unavailability')
      .select('*')
      .eq('host_id', user.id)
      .order('start_date');
    setBlocks((data ?? []) as HostUnavailability[]);
    setLoading(false);
  }

  // eslint-disable-next-line react-hooks/exhaustive-deps
  useEffect(() => { void load(); }, [user]);

  const today = todayYmd();

  function isBlocked(date: string): boolean {
    return blocks.some((b) => date >= b.start_date && date <= b.end_date);
  }

  async function addBlock(start: string, end: string) {
    if (!user) return;
    setSaving(true);
    await supabase.from('host_unavailability').insert({ host_id: user.id, start_date: start, end_date: end });
    setPendingStart(null);
    setClash(await bookedStaysHit(user.id, start, end));
    await load();
    setSaving(false);
  }

  async function removeBlock(id: string) {
    await supabase.from('host_unavailability').delete().eq('id', id);
    await load();
  }

  function handleDayClick(date: string) {
    if (date < today) return; // can't block the past
    if (isBlocked(date)) return; // existing blocks are removed from the list below
    if (!pendingStart) {
      setPendingStart(date);
    } else {
      const start = date < pendingStart ? date : pendingStart;
      const end = date < pendingStart ? pendingStart : date;
      void addBlock(start, end);
    }
  }

  const cells = monthGrid(cursor);

  if (loading) return <div className="flex justify-center py-16"><Spinner /></div>;

  return (
    <div className="min-h-dvh bg-app-bg text-app-fg">
      <div className="px-6 md:px-10 py-8 md:py-12">
        <div className="mb-6">
          <h1 className="text-2xl md:text-3xl font-semibold text-app-fg mb-1">Availability</h1>
          <p className="text-app-muted text-sm">Block the dates you can't host. Travelers whose trip overlaps a blocked range won't be matched with you.</p>
        </div>

        {clash.length > 0 && (
          <div role="alert" className="mb-4 rounded-xl border border-gold/30 bg-gold/10 p-4 text-sm text-app-fg">
            These dates overlap {clash.length === 1 ? 'a trip' : 'trips'} you already have
            {' '}({clash.map((s) => `${s.city ?? 'trip'} from ${formatRange(s.start, s.start)}`).join(', ')}).
            Nothing was cancelled. Those travelers still expect you, so write to Sekretly if you can't host them.
          </div>
        )}

        {/* Calendar */}
        <div className="bg-app-card border border-app-border rounded-2xl p-4 md:p-5">
          <div className="flex items-center justify-between mb-4">
            <button onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() - 1, 1))}
              className="w-9 h-9 rounded-lg flex items-center justify-center text-app-muted hover:text-app-fg hover:bg-black/5 transition-colors" aria-label="Previous month">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M15.75 19.5L8.25 12l7.5-7.5" />
              </svg>
            </button>
            <p className="text-sm font-semibold text-app-fg">{MONTHS[cursor.getMonth()]} {cursor.getFullYear()}</p>
            <button onClick={() => setCursor((c) => new Date(c.getFullYear(), c.getMonth() + 1, 1))}
              className="w-9 h-9 rounded-lg flex items-center justify-center text-app-muted hover:text-app-fg hover:bg-black/5 transition-colors" aria-label="Next month">
              <svg xmlns="http://www.w3.org/2000/svg" fill="none" viewBox="0 0 24 24" strokeWidth={2} stroke="currentColor" className="w-4 h-4">
                <path strokeLinecap="round" strokeLinejoin="round" d="M8.25 4.5l7.5 7.5-7.5 7.5" />
              </svg>
            </button>
          </div>

          <div className="grid grid-cols-7 gap-1 mb-1">
            {WEEKDAYS.map((w, i) => (
              <div key={i} className="text-center text-[10px] font-medium text-app-muted py-1">{w}</div>
            ))}
          </div>

          <div className="grid grid-cols-7 gap-1">
            {cells.map((date, i) => {
              if (!date) return <div key={i} />;
              const ds = ymd(date);
              const past = ds < today;
              const blocked = isBlocked(ds);
              const isPending = pendingStart === ds;
              return (
                <button
                  key={i}
                  onClick={() => handleDayClick(ds)}
                  disabled={past || (blocked && !isPending)}
                  className={`aspect-square rounded-lg text-sm flex items-center justify-center transition-colors ${
                    past ? 'text-app-muted/30 cursor-not-allowed' :
                    blocked ? 'bg-crimson/15 text-crimson cursor-not-allowed' :
                    isPending ? 'bg-gold text-ink font-semibold' :
                    'text-app-fg hover:bg-black/5'
                  }`}
                >
                  {date.getDate()}
                </button>
              );
            })}
          </div>

          <p className="text-xs text-app-muted mt-4">
            {pendingStart
              ? <>Start set to <span className="text-app-fg font-medium">{formatRange(pendingStart, pendingStart)}</span> — tap the end date to block the range. <button onClick={() => setPendingStart(null)} className="text-gold underline underline-offset-2 ml-1">Cancel</button></>
              : 'Tap a start date, then an end date to block a range. Tap a single day twice to block just that day.'}
          </p>
          {saving && <p className="text-xs text-app-muted mt-1">Saving…</p>}
        </div>

        {/* Blocked ranges list */}
        <div className="mt-6">
          <h2 className="text-sm font-semibold text-app-fg mb-3">Blocked dates</h2>
          {blocks.length === 0 ? (
            <p className="text-sm text-app-muted">Nothing blocked — you're available for any trip.</p>
          ) : (
            <div className="flex flex-col gap-2">
              {blocks.map((b) => (
                <div key={b.id} className="flex items-center justify-between gap-3 bg-app-card border border-app-border rounded-xl px-4 py-3">
                  <div className="flex items-center gap-2.5 min-w-0">
                    <span className="w-2 h-2 rounded-full bg-crimson shrink-0" />
                    <span className="text-sm text-app-fg truncate">{formatRange(b.start_date, b.end_date)}</span>
                  </div>
                  <button onClick={() => void removeBlock(b.id)} className="text-xs text-app-muted hover:text-crimson transition-colors shrink-0">Remove</button>
                </div>
              ))}
            </div>
          )}
        </div>
      </div>
    </div>
  );
}

export default Availability;
